import type { KPIPeriodFilter } from "../../domain/types";
import type { KPIOverviewExplainArgs } from "../api/queries";

/** search params ສຳລັບ `/app/management/okr-kpi/explain` */
export type KPIOverviewExplainPageSearch = {
  periodType: "monthly" | "quarterly";
  year: number;
  month?: number;
  quarter?: number;
  departmentId?: string;
  q?: string;
  scope: "organization" | "department" | "position" | "employee";
  scopeDepartmentId?: string;
  scopePositionId?: string;
  kpiId?: string;
  /** Phase B: ສະຫຼຸບແຫຼ່ງຂໍ້ມູນ (ຫຼື ມີ kpiId ແລ້ວ API ເປີດໃຫ້ອັດຕະໂນມັດ) */
  includeSources?: boolean;
  /** Phase C: ຕົວຢ່າງບັນທຶກພ້ອມລິ້ງ */
  includeRecordSamples?: boolean;
  recordSampleLimit?: number;
};

export function parseKpiOverviewExplainSearch(
  search: Record<string, unknown>,
): KPIOverviewExplainPageSearch {
  const periodType =
    search.periodType === "quarterly" ? "quarterly" : "monthly";
  const yearRaw = Number(search.year);
  const year =
    Number.isFinite(yearRaw) && yearRaw >= 2000 && yearRaw <= 2100
      ? yearRaw
      : new Date().getFullYear();
  const monthRaw = search.month != null ? Number(search.month) : undefined;
  const month =
    monthRaw != null &&
    Number.isFinite(monthRaw) &&
    monthRaw >= 1 &&
    monthRaw <= 12
      ? monthRaw
      : undefined;
  const quarterRaw =
    search.quarter != null ? Number(search.quarter) : undefined;
  const quarter =
    quarterRaw != null &&
    Number.isFinite(quarterRaw) &&
    quarterRaw >= 1 &&
    quarterRaw <= 4
      ? quarterRaw
      : undefined;
  const scopeRaw = search.scope;
  const scope =
    scopeRaw === "department" ||
    scopeRaw === "employee" ||
    scopeRaw === "position"
      ? scopeRaw
      : "organization";
  const departmentId =
    typeof search.departmentId === "string" && search.departmentId.trim()
      ? search.departmentId.trim()
      : undefined;
  const q =
    typeof search.q === "string" && search.q.trim()
      ? search.q.trim()
      : undefined;
  const scopeDepartmentId =
    typeof search.scopeDepartmentId === "string" &&
    search.scopeDepartmentId.trim()
      ? search.scopeDepartmentId.trim()
      : undefined;
  const scopePositionId =
    typeof search.scopePositionId === "string" &&
    search.scopePositionId.trim()
      ? search.scopePositionId.trim()
      : undefined;
  const kpiId =
    typeof search.kpiId === "string" && search.kpiId.trim()
      ? search.kpiId.trim()
      : undefined;

  const rawSources = search.includeSources;
  const includeSources =
    rawSources === true ||
    rawSources === "true" ||
    rawSources === "1" ||
    rawSources === 1;

  const rawRec = search.includeRecordSamples;
  const includeRecordSamples =
    rawRec === true ||
    rawRec === "true" ||
    rawRec === "1" ||
    rawRec === 1;

  const limitRaw =
    search.recordSampleLimit != null ? Number(search.recordSampleLimit) : undefined;
  const recordSampleLimit =
    limitRaw != null &&
    Number.isFinite(limitRaw) &&
    limitRaw >= 1 &&
    limitRaw <= 50
      ? Math.floor(limitRaw)
      : undefined;

  return {
    periodType,
    year,
    month:
      periodType === "monthly"
        ? (month ?? new Date().getMonth() + 1)
        : undefined,
    quarter: periodType === "quarterly" ? (quarter ?? 1) : undefined,
    departmentId,
    q,
    scope,
    scopeDepartmentId,
    scopePositionId,
    kpiId,
    includeSources: includeSources || undefined,
    includeRecordSamples: includeRecordSamples || undefined,
    recordSampleLimit,
  };
}

export function searchToPeriodFilter(
  s: KPIOverviewExplainPageSearch,
): KPIPeriodFilter {
  return {
    periodType: s.periodType,
    year: s.year,
    month: s.periodType === "monthly" ? s.month : undefined,
    quarter: s.periodType === "quarterly" ? s.quarter : undefined,
    departmentId: s.departmentId,
    search: s.q,
  };
}

export function searchToExplainArgs(
  s: KPIOverviewExplainPageSearch,
): KPIOverviewExplainArgs {
  return {
    scope: s.scope,
    scopeDepartmentId: s.scopeDepartmentId,
    scopePositionId: s.scopePositionId,
    kpiId: s.kpiId,
    includeSources: s.includeSources,
    includeRecordSamples: s.includeRecordSamples,
    recordSampleLimit: s.recordSampleLimit,
  };
}

export function buildKpiOverviewExplainSearch(
  filter: KPIPeriodFilter,
  args: KPIOverviewExplainArgs,
): KPIOverviewExplainPageSearch {
  return {
    periodType: filter.periodType,
    year: filter.year,
    month: filter.periodType === "monthly" ? filter.month : undefined,
    quarter: filter.periodType === "quarterly" ? filter.quarter : undefined,
    departmentId: filter.departmentId?.trim() || undefined,
    q: filter.search?.trim() || undefined,
    scope: args.scope,
    scopeDepartmentId: args.scopeDepartmentId,
    scopePositionId: args.scopePositionId,
    kpiId: args.kpiId,
    includeSources: args.includeSources || undefined,
    includeRecordSamples: args.includeRecordSamples || undefined,
    recordSampleLimit: args.recordSampleLimit,
  };
}
